import React, { useState, useEffect } from 'react'
import { Grid, Typography, Divider, Select } from '@the-platform-group/component-library'
import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { Button } from '@the-platform-group/component-library/Button'
import { Modal, ModalContent } from '@the-platform-group/component-library/Modal'
import focuses from 'constants/focus'
import { getColumnValues, getRowValues } from './SecurityTableValues'

const sectorOptions = Object.keys(focuses.SECTORS).map(sector => ({
  label: focuses.SECTORS[sector],
  value: sector,
}))

const subSectorOptions = sector => {
  const subSectorsForSector = Object.keys(focuses.SUBSECTOR_GROUPS).find(group => group === sector)
  const subSectors = focuses.SUBSECTOR_GROUPS[subSectorsForSector]
  return Object.keys(subSectors).map(subSector => ({
    label: subSectors[subSector],
    value: subSector,
  }))
}

function SecuritiesTable({
  securities,
  isLoading,
  isSaving,
  isDeleting,
  saveOverride,
  removeOverride,
  pagination,
  count,
  handlePageChange,
  handlePageSizeChange,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [sectorSelected, setSectorSelected] = useState('')
  const [subSectorSelected, setSubSectorSelected] = useState('')
  const [selectedSecurity, setSelectedSecurity] = useState(null)
  const { pageSize, page } = pagination

  function closeModal() {
    setSelectedSecurity(null)
    setSectorSelected('')
    setSubSectorSelected('')
    setIsOpen(false)
  }

  const openModal = security => {
    const { picsSectorCode, picsSubsectorCode } = security.issuer.sectorOverride || {}

    setSectorSelected(picsSectorCode || '')
    setSubSectorSelected(picsSubsectorCode || '')
    setSelectedSecurity(security)
    setIsOpen(true)
  }

  useEffect(() => {
    // Prefilling the modal with an override if exists can trigger the
    // subsector to be reset when the initial state of the sector is set.
    if (!sectorSelected || !selectedSecurity) {
      setSubSectorSelected('')
    }
  }, [sectorSelected, selectedSecurity])

  const handleOverrideSave = () => {
    saveOverride({
      sector: sectorSelected,
      subsector: subSectorSelected,
      security: selectedSecurity,
    })
    closeModal()
  }

  const handleOverrideDelete = () => {
    removeOverride({ issuerLegacyId: selectedSecurity.issuer.legacyId })
    closeModal()
  }

  return (
    <>
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={12}>
          <DataGrid
            columns={getColumnValues(openModal)}
            commonColumnProps={{ headerAlign: 'left' }}
            loading={isLoading || isSaving || isDeleting}
            name="ds1Securities"
            rows={getRowValues(securities)}
            // pagination
            page={page}
            pageSize={pageSize}
            rowCount={count}
            pageSizeOptions={[5, 10, 25, 50]}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Grid>
      </Grid>

      <Modal open={isOpen} onClose={closeModal}>
        <ModalContent>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography variant="h4">Overrides</Typography>
            </Grid>
            <Grid item>
              <Divider />
            </Grid>
            <Grid item container>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Sector</Typography>
              </Grid>
              <Grid item xs={12}>
                <Select
                  fullWidth
                  value={sectorSelected}
                  options={sectorOptions}
                  onChange={e => setSectorSelected(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid item container>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Sub Sector</Typography>
              </Grid>
              <Grid item xs={12}>
                <Select
                  fullWidth
                  value={subSectorSelected}
                  options={sectorSelected ? subSectorOptions(sectorSelected) : []}
                  onChange={e => setSubSectorSelected(e.target.value)}
                  disabled={!sectorSelected}
                />
              </Grid>
            </Grid>
            <Grid item container justifyContent="space-between" spacing={2}>
              <Grid item container xs={6}>
                {!!selectedSecurity && !!selectedSecurity.issuer.sectorOverride && (
                  <Button
                    variant="contained"
                    color="error"
                    isLoading={isDeleting}
                    disabled={isSaving}
                    onClick={handleOverrideDelete}
                  >
                    DELETE
                  </Button>
                )}
              </Grid>
              <Grid item container justifyContent="flex-end" xs={6}>
                <Grid item>
                  <Button variant="text" onClick={closeModal}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button isLoading={isSaving} disabled={isDeleting} onClick={handleOverrideSave}>
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ModalContent>
      </Modal>
    </>
  )
}

export default SecuritiesTable
